import React, { useLayoutEffect, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BarcodeScanner from '../components/BarcodeScanner';
import {
  postScanBarcode,
  getScannerPrefs,
  upsertScannerPrefs,
  type ScanSuccess,
  type PrefsOut,
} from '../api/endpoints';
import {
  postScannerStart,
  postScannerPrefsView,
  postScannerPrefsUpdate,
} from '../api/telemetry';
import useScanCredits from '../hooks/useScanCredits';

import { IconRefresh, IconGear, IconBarcode } from '../components/icons/AnalyzerIcons';
import WaitingGame from '../components/WaitingGame';
import ImageScannerButton, { type ImageScannerResult } from '../components/ImageScannerButton';
import ImageScannerCard from '../components/ImageScannerCard';
import ProductConfirmationDialog, { type ProductConfirmationData } from '../components/ProductConfirmationDialog';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';

export interface ProductSoloInput {
  /** Optional pre-filled barcode (when arriving from a tool call). */
  barcode?: string;
}

export interface ProductSoloResult {
  product_id: number;
  barcode: string;
  source?: string;
}

/* ── Motion constants ───────────────────────────────── */
const spring: Transition = { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 };
const quick: Transition = { duration: 0.18 };
const pageVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
const sectionItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: spring },
};

/* ── Micro-UX helpers ───────────────────────────────── */
function AnimatedSwap({
  value,
  className,
  as: Tag = 'span',
}: { value: React.ReactNode; className?: string; as?: React.ElementType }) {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={typeof value === 'string' || typeof value === 'number' ? String(value) : 'swap'}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.18 }}
        className={className}
      >
        <Tag>{value}</Tag>
      </motion.span>
    </AnimatePresence>
  );
}

export function FlashHalo({
  children,
  depsKey,
  className,
  color = '42,164,166',
  intensity = 0.28,
  duration = 0.8,
}: {
  children: React.ReactNode;
  depsKey: React.Key;
  className?: string;
  color?: string;
  intensity?: number;
  duration?: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [radius, setRadius] = useState<string>('12px');
  const [display, setDisplay] = useState<'block' | 'inline-block'>('inline-block');

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const child = (wrap.firstElementChild ?? wrap) as HTMLElement;
    const d = getComputedStyle(child).display;
    setDisplay(d === 'inline' || d === 'inline-block' || child.tagName === 'SPAN' ? 'inline-block' : 'block');
    const br = getComputedStyle(child).borderRadius;
    if (br && br !== '0px') {
      setRadius(br);
    } else {
      const rect = child.getBoundingClientRect();
      const pillLikely = rect.width > rect.height * 1.8;
      setRadius(pillLikely ? '9999px' : '12px');
    }
  }, [children]);

  return (
    <div ref={wrapRef} className={`relative isolate ${className ?? ''}`} style={{ display }}>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={String(depsKey)}
          initial={{ opacity: 0.9, scale: 0.985 }}
          animate={{ opacity: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration, ease: 'easeOut' }}
          className="pointer-events-none absolute -inset-1 will-change-[opacity,transform]"
          aria-hidden
          style={{
            borderRadius: radius,
            background: `radial-gradient(120% 120% at 50% 50%, rgba(${color}, ${intensity}) 0%, rgba(${color}, 0) 60%)`,
            filter: 'blur(12px)',
          }}
        />
      </AnimatePresence>
      {children}
    </div>
  );
}

const ALLERGENS = [
  'nickel','benzoic_acid','methylisothiazolinone','methylchloroisothiazolinone','parabens','lanolin',
  'fragrance','linalool','limonene','citral','hydroxycitronellal','balsam_peru','tocopherol_allergy',
] as const;
type Allergen = typeof ALLERGENS[number];

const SENS_OPTIONS = [
  { v: 'none', label: 'None' },
  { v: 'possible', label: 'Possible' },
  { v: 'likely', label: 'Likely' },
] as const;

const ETHICAL_OPTIONS = [
  { v: 'off', label: 'Off' },
  { v: 'vegan', label: 'Vegan' },
  { v: 'halal', label: 'Halal' },
  { v: 'cruelty_free', label: 'Cruelty-free' },
] as const;

const labelize = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

function Toggle({
  label, hint, checked, onChange,
}: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="card card--compact card--bordered flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
        {hint && <div className="t-small t-mute">{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => { onChange(!checked); }}
        className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
        style={{ background: checked ? 'var(--c-kivo-teal)' : 'var(--c-charcoal-12)' }}
        aria-pressed={checked}
      >
        <span
          className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition duration-200"
          style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  );
}

function PreferencesPage({ onBack }: { onBack: () => void }) {
  const prefersReduced = useReducedMotion() ?? false;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [allergies, setAllergies] = useState<Allergen[]>([]);
  const [sensitivity, setSensitivity] = useState<'none' | 'possible' | 'likely'>('likely');
  const [acneProne, setAcneProne] = useState(false);
  const [ethical, setEthical] = useState<'off' | 'vegan' | 'halal' | 'cruelty_free'>('off');
  const [derm, setDerm] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const p: PrefsOut = await getScannerPrefs();
        const hasTracked = sessionStorage.getItem('telemetry_scanner_prefs_viewed');
        if (!hasTracked) {
          void postScannerPrefsView().catch(function ignoreError() { /* non-fatal */ });
          sessionStorage.setItem('telemetry_scanner_prefs_viewed', '1');
        }
        setAllergies(p.allergies as Allergen[]);
        setSensitivity(p.sensitivity);
        setAcneProne(p.acne_prone);
        setEthical(p.ethical_mode);
        setDerm(p.cautious_actives);
      } catch (error: unknown) {
        setErr(getErrorMessage(error, 'Failed to load preferences'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleAllergen = (a: Allergen) =>
    { setAllergies((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a])); };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await upsertScannerPrefs({
        allergies,
        sensitivity,
        acne_prone: acneProne,
        ethical_mode: ethical,
        cautious_actives: derm,
      });
      void postScannerPrefsUpdate([]).catch(function ignoreError() { /* non-fatal */ });
      onBack();
    } catch (error: unknown) {
      setErr(getErrorMessage(error, 'Failed to save'));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl py-4 sm:py-6">
      <div
        className="sticky top-0 z-10 px-4 sm:px-0 py-3"
        style={{ background: 'var(--c-cream-weak)', borderBottom: '1px solid var(--rule)', backdropFilter: 'blur(8px)' }}
      >
        <div className="flex items-center justify-between">
          <h1 className="t-h3">Scanner preferences</h1>
          <div className="row-3">
            <motion.button
              onClick={onBack}
              whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
              className="btn btn--ghost btn--sm"
            >
              Skip
            </motion.button>
            <motion.button
              onClick={save}
              disabled={saving}
              whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
              className="btn btn--primary btn--sm"
            >
              <AnimatedSwap value={saving ? 'Saving…' : 'Save'} />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-0 pt-4">
        <div className="card card--sand card--compact">
          <span className="t-small" style={{ fontWeight: 500 }}>Optional.</span>
          <span className="t-small t-mute"> Personalize your preference scoring — or skip and set them any time.</span>
        </div>
      </div>

      <div className="px-4 sm:px-0 py-4 stack-5">
        {loading ? (
          <p className="t-small t-mute">Loading…</p>
        ) : (
          <>
            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ingredients to avoid</div>
              <p className="t-small t-mute">Select any known allergens to flag risky products.</p>
              <div className="flex flex-wrap gap-2">
                {ALLERGENS.map((a) => {
                  const active = allergies.includes(a);
                  return (
                    <motion.button
                      key={a}
                      onClick={() => { toggleAllergen(a); }}
                      whileTap={{ scale: prefersReduced ? 1 : 0.96 }}
                      className={active ? 'pill pill--teal' : 'pill'}
                      style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                    >
                      {active && <span className="dot" />}
                      <AnimatedSwap value={labelize(a)} />
                    </motion.button>
                  );
                })}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Skin sensitivity</div>
              <p className="t-small t-mute">How strict should we be about potential irritants?</p>
              <div
                className="grid grid-cols-3 w-full overflow-hidden"
                style={{ borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }}
              >
                {SENS_OPTIONS.map((o, i) => (
                  <button
                    key={o.v}
                    onClick={() => { setSensitivity(o.v); }}
                    className="px-3 py-2 t-small text-center"
                    style={{
                      borderLeft: i > 0 ? '1px solid var(--rule)' : undefined,
                      background: sensitivity === o.v ? 'var(--c-kivo-teal)' : 'var(--c-cream)',
                      color: sensitivity === o.v ? 'var(--c-cream)' : 'var(--ink)',
                      transition: 'background var(--t-fast) var(--ease)',
                    }}
                  >
                    <AnimatedSwap value={o.label} />
                  </button>
                ))}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ethical preference</div>
              <p className="t-small t-mute">Pick one.</p>
              <div className="flex flex-wrap gap-2">
                {ETHICAL_OPTIONS.map((o) => {
                  const active = ethical === o.v;
                  return (
                    <motion.button
                      key={o.v}
                      onClick={() => { setEthical(o.v); }}
                      whileTap={{ scale: prefersReduced ? 1 : 0.96 }}
                      className={active ? 'pill pill--teal' : 'pill'}
                      style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                    >
                      {active && <span className="dot" />}
                      <AnimatedSwap value={o.label} />
                    </motion.button>
                  );
                })}
              </div>
            </section>

            <section className="grid sm:grid-cols-3 gap-3">
              <Toggle label="Breakout-prone" hint="Prefer non-comedogenic picks." checked={acneProne} onChange={setAcneProne} />
              <Toggle label="Prefer gentle formulas" hint="Go easy on strong actives." checked={derm} onChange={setDerm} />
            </section>

            <AnimatePresence>
              {err && (
                <motion.div
                  key={err}
                  initial={{ y: -6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -6, opacity: 0 }}
                  transition={quick}
                  className="t-small"
                  style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                >
                  {err}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
}

const ProductSoloScreen: React.FC<ScreenProps<ProductSoloInput, ProductSoloResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const nav = useNavigate();
  const prefersReduced = useReducedMotion() ?? false;
  const [barcode, setBarcode] = useState(input?.barcode ?? '');
  const [useManual, setUseManual] = useState(!!input?.barcode);
  const [scannerKey, setScannerKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [gameVisible, setGameVisible] = useState(false);
  const gameTimerRef = useRef<number | null>(null);
  const [confirmationProduct, setConfirmationProduct] = useState<ProductConfirmationData | null>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Credit gating (cost comes from the API via the user context — never hardcoded).
  const { total: creditsLeft, outOfQuota, isPlusOrPremium } = useScanCredits('product_barcode');
  const scansLeft = creditsLeft;

  useEffect(() => {
    if (mode !== 'page') return;
    void (async () => {
      try {
        const p: PrefsOut = await getScannerPrefs();
        const looksDefault =
          p.allergies.length === 0 &&
          p.sensitivity === 'likely' &&
          !p.acne_prone &&
          p.ethical_mode === 'off' &&
          !p.cautious_actives;
        const shown = localStorage.getItem('prefsPagePromptShown');
        if (looksDefault && !shown) {
          localStorage.setItem('prefsPagePromptShown', '1');
          setShowPrefs(true);
        }
      } catch { /* ignore */ }
    })();
  }, [mode]);

  const onFallback = useCallback(() => { setUseManual(true); }, []);
  const onDetected = useCallback((bc: string) => { setBarcode(bc.replace(/\D/g, '')); }, []);
  const digits = barcode.replace(/\D/g, '');
  const canSubmit = digits.length >= 8 && digits.length <= 14;

  const restartScanner = () => {
    setUseManual(false);
    setScannerKey((k) => k + 1);
    setError(null);
    setBarcode('');
  };

  const submit = async () => {
    if (!canSubmit || submitting || outOfQuota) return;
    setSubmitting(true);
    setError(null);
    void postScannerStart(digits).catch(function ignoreError() { /* non-fatal */ });

    if (gameTimerRef.current) window.clearTimeout(gameTimerRef.current);
    gameTimerRef.current = window.setTimeout(() => { setGameVisible(true); }, 600);

    try {
      const res: ScanSuccess = await postScanBarcode({ barcode: digits });
      if (!res.ok) {
        setSubmitting(false);
        setGameVisible(false);
        if (gameTimerRef.current) window.clearTimeout(gameTimerRef.current);
        setError(res.message ?? 'Invalid or banned barcode');
        return;
      }
      const result: ProductSoloResult = {
        product_id: res.product_id,
        barcode: res.barcode,
        source: res.source,
      };
      if (mode === 'modal') {
        setSubmitting(false);
        setGameVisible(false);
        onComplete?.(result);
      } else {
        nav(`/scanner/result/${res.product_id}`, {
          state: { barcode: result.barcode, source: res.source },
        });
      }
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Scan failed'));
      setSubmitting(false);
      setGameVisible(false);
    } finally {
      if (gameTimerRef.current) window.clearTimeout(gameTimerRef.current);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void submit();
  };

  const handleImageScanResult = (result: ImageScannerResult) => {
    setConfirmationProduct(result);
  };
  const handleImageScanError = (errorMsg: string) => {
    setError(errorMsg);
  };
  const handleConfirmProduct = () => {
    if (!confirmationProduct) return;
    const { barcode: scannedBarcode, product_id } = confirmationProduct;
    setConfirmationProduct(null);
    setError(null);

    // Slug-only product (no barcode, e.g. local brands): it already exists in
    // the DB after the image scan — go straight to the result by product id.
    if (!scannedBarcode) {
      const result: ProductSoloResult = { product_id, barcode: '', source: 'ai' };
      if (mode === 'modal') {
        onComplete?.(result);
      } else {
        nav(`/scanner/result/${product_id}`, { state: { barcode: '', source: 'ai' } });
      }
      return;
    }

    setBarcode(scannedBarcode);
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
      barcodeInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const flash = document.createElement('div');
      flash.className = 'absolute inset-0 rounded-lg pointer-events-none';
      flash.style.background = 'var(--c-teal-weak)';
      flash.style.animation = 'fadeOut 0.8s ease-out forwards';
      const inputParent = barcodeInputRef.current.parentElement;
      if (inputParent) {
        inputParent.style.position = 'relative';
        inputParent.appendChild(flash);
        setTimeout(() => { flash.remove(); }, 800);
      }
    }
  };
  const handleCancelProduct = () => { setConfirmationProduct(null); };

  const hero = useMemo(
    () => (
      <div className="card card--bordered relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, var(--c-teal-weak) 0%, transparent 65%)' }}
        />
        <div className="relative p-4 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div
            className="grid place-items-center w-9 h-9 rounded-full shrink-0"
            style={{ background: 'var(--c-teal-weak)', color: 'var(--c-kivo-teal)' }}
          >
            <IconBarcode />
          </div>
          <div className="min-w-0">
            <h1 className="t-h3">Scan a Product</h1>
            <p className="t-small t-mute">
              Point your camera at the barcode or enter it manually. We&apos;ll fetch brand, name, and ingredients.
            </p>
            {mode === 'page' && (
              <button
                type="button"
                onClick={() => { nav('/scanner/how-it-works'); }}
                className="mt-2 inline-flex items-center gap-1 t-small"
                style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline' }}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 18h.01" />
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 6a2 2 0 0 1 2 2c0 1.5-2 2-2 3" />
                </svg>
                Find more about how this works
              </button>
            )}
          </div>

          <div className="mt-2 sm:mt-0 sm:ml-auto flex items-center gap-2">
            {scansLeft !== null && (
              <FlashHalo depsKey={scansLeft}>
                <span
                  className={outOfQuota ? 'pill' : 'pill pill--teal'}
                  style={outOfQuota ? { color: 'var(--c-error)' } : undefined}
                  title={outOfQuota ? 'Out of credits' : `${scansLeft} credits left`}
                >
                  {outOfQuota
                    ? <><span className="dot dot--coral" /><AnimatedSwap value="0 credits" /></>
                    : <><span className="dot" /><AnimatedSwap value={`${scansLeft} credits`} /></>
                  }
                </span>
              </FlashHalo>
            )}
            <button
              onClick={() => { setShowPrefs(true); }}
              className="btn btn--ghost btn--sm"
            >
              <span className="sm:hidden"><IconGear /></span>
              <span className="hidden sm:inline-flex items-center gap-1"><IconGear /> Preferences</span>
            </button>
          </div>
        </div>
      </div>
    ),
    [outOfQuota, scansLeft, nav, mode],
  );

  const mainView = (
    <motion.div
      key="main"
      className="stack-5"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={sectionItem}>
        {hero}
      </motion.div>

      <motion.div variants={sectionItem}>
        <ImageScannerCard
          onResult={handleImageScanResult}
          onError={handleImageScanError}
          disabled={submitting || outOfQuota}
          isPlusOrPremium={isPlusOrPremium}
        />
      </motion.div>

      <motion.div variants={sectionItem}>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 card card--bordered p-4 sm:p-5">
            {!useManual ? (
              <>
                <div
                  className="aspect-[16/9] overflow-hidden"
                  style={{ borderRadius: 'var(--r-md)', background: 'var(--c-charcoal)' }}
                >
                  <BarcodeScanner key={scannerKey} onDetected={onDetected} onFallback={onFallback} />
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  <FlashHalo depsKey={digits}>
                    <div className="t-small">
                      <span className="t-mute">Detected:</span>{' '}
                      <AnimatedSwap value={<span className="font-mono font-semibold">{digits || '—'}</span>} />
                    </div>
                  </FlashHalo>
                  <div className="flex gap-3 items-center">
                    <ImageScannerButton
                      onResult={handleImageScanResult}
                      onError={handleImageScanError}
                      disabled={submitting || outOfQuota}
                      isPlusOrPremium={isPlusOrPremium}
                      variant="icon"
                    />
                    <button
                      onClick={restartScanner}
                      className="inline-flex items-center gap-1 t-small"
                      style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', textUnderlineOffset: '4px' }}
                    >
                      <IconRefresh /> Rescan
                    </button>
                    <button
                      onClick={() => { setUseManual(true); }}
                      className="t-small"
                      style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', textUnderlineOffset: '4px' }}
                    >
                      Switch to manual
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="stack-3">
                <div className="flex items-center justify-between">
                  <div className="t-small t-mute">Enter barcode (EAN-8 / UPC-A / EAN-13 / GTIN-14)</div>
                  <ImageScannerButton
                    onResult={handleImageScanResult}
                    onError={handleImageScanError}
                    disabled={submitting || outOfQuota}
                    isPlusOrPremium={isPlusOrPremium}
                    variant="icon"
                    className="shrink-0"
                  />
                </div>
                <input
                  ref={barcodeInputRef}
                  value={barcode}
                  onChange={(e) => { setBarcode(e.target.value.replace(/\D/g, '')); }}
                  onKeyDown={onKeyDown}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="e.g. 0608614693054"
                  className="w-full p-3 t-body outline-none transition-all"
                  style={{
                    borderRadius: 'var(--r-sm)',
                    border: '1px solid var(--rule)',
                    background: 'var(--c-cream)',
                    color: 'var(--ink)',
                  }}
                />
                <div className="flex items-center justify-between">
                  <p className="t-small t-mute">No camera or disabled — manual entry enabled.</p>
                  <button
                    onClick={restartScanner}
                    className="inline-flex items-center gap-1 t-small"
                    style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', textUnderlineOffset: '4px' }}
                  >
                    <IconRefresh /> Back to scanner
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="card card--bordered p-4 sm:p-5 stack-3">
            {outOfQuota ? (
              <div className="text-center stack-3">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-full mx-auto"
                  style={{ background: 'rgba(196,103,58,0.1)', color: 'var(--c-error)' }}
                >✕</div>
                <p className="t-small" style={{ fontWeight: 600, color: 'var(--ink)' }}>You&apos;re all set for today</p>
                <p className="t-small t-mute">You&apos;re out of credits. Top up in your wallet or wait for your monthly reset.</p>
                <div className="flex items-center justify-center gap-2">
                  <button onClick={() => { nav('/plans'); }} className="btn btn--primary btn--sm">Upgrade plan</button>
                  <button onClick={() => { if (mode === 'modal') onCancel?.(); else nav('/'); }} className="btn btn--ghost btn--sm">
                    {mode === 'modal' ? 'Close' : "I&apos;ll try tomorrow"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="t-small t-mute">When ready, submit the scan.</div>
                <motion.button
                  onClick={submit}
                  disabled={!canSubmit || submitting}
                  whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                  className="btn btn--primary btn--full"
                >
                  <AnimatedSwap value={submitting ? 'Contacting AI…' : 'Submit'} />
                </motion.button>

                <AnimatePresence initial={false}>
                  {submitting && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="card card--sand card--compact t-small t-mute"
                    >
                      First-time products may take longer while we ingest details.
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {error && (
                    <motion.div
                      key={error}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={quick}
                      className="stack-2"
                    >
                      <div
                        className="t-small"
                        style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                      >
                        {error}
                      </div>
                      <button
                        onClick={restartScanner}
                        className="btn btn--ghost btn--sm btn--full row-3 justify-center"
                      >
                        <IconRefresh /> Try scanning again
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div
                  className="pt-2 t-small t-mute"
                  style={{ borderTop: '1px solid var(--rule)' }}
                >
                  Tip: Most retail barcodes are 8, 12, 13, or 14 digits. Remove spaces and letters.
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {gameVisible && submitting && (
        <WaitingGame onExit={() => { setGameVisible(false); }} />
      )}

      {confirmationProduct && (
        <ProductConfirmationDialog
          product={confirmationProduct}
          onConfirm={handleConfirmProduct}
          onCancel={handleCancelProduct}
        />
      )}
    </motion.div>
  );

  const inner = (
    <AnimatePresence mode="wait" initial={false}>
      {showPrefs ? (
        <motion.div
          key="prefs"
          initial={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          transition={spring}
        >
          <PreferencesPage onBack={() => { setShowPrefs(false); }} />
        </motion.div>
      ) : (
        mainView
      )}
    </AnimatePresence>
  );

  if (mode === 'modal') {
    return (
      <ModalShell
        title="Scan a product"
        onClose={() => onCancel?.()}
        widthClass="max-w-4xl"
      >
        <div className="p-4 sm:p-5">{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default ProductSoloScreen;
